import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { CountdownTimer } from "../../../components/inputs/countdownTimer";
import { TextInput } from "../../../components/inputs/textInput";
import { InfoComponent } from "../../../components/notification/infoComponent";
import { AuthHeader } from "../../../components/typography/authHeader";
import { OrText } from "../../../components/typography/orText";
import { useSocialAuth } from "../../../customeHooks/useSocialAuth";
import {
  useResendOtpMutation,
  useSignUpMutation,
  useVerifyEmailMutation,
} from "../../../redux/features/auth/auth.api";
import { authSliceActions } from "../../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../../redux/hooks";
import { BaseApiErrorResponse } from "../../../utils/types";
import { SignUpSchema, VerifySignUpSchema } from "../../../utils/validations";

const Signup = ({
  step,
  setStep,
  setEmail,
  searchParams,
}: {
  step: number;
  setStep: (step: number) => void;
  setEmail: (email: string) => void;
  searchParams?: URLSearchParams;
}) => {
  const [signup, { isLoading, isError, error }] = useSignUpMutation();

  const navigate = useNavigate();

  const {
    loginWighGoogle,
    isGoogleAuthError,
    authenticatingWithGoogle,
    googleAuthError,
  } = useSocialAuth();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        firstName: searchParams?.get("firstName") ?? "",
        lastName: searchParams?.get("lastName") ?? "",
        email: searchParams?.get("email") ?? "",
        password: "",
      },
      validationSchema: SignUpSchema,
      onSubmit: async (vals) => {
        const response = await signup({ ...vals });

        if (response.data?.status === "success") {
          setEmail(vals.email);
          setStep(step + 1);
        } else if (
          (response.error as BaseApiErrorResponse)?.data?.message
            ?.toLowerCase()
            ?.trim() === "please activate your account"
        ) {
          navigate(
            `/signup?step=verify&email=${encodeURIComponent(values.email)}`,
          );
        }
      },
    });
  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Create an account"
        text={
          <span>
            Sign up to Tikket to create events or{" "}
            <Link
              to={"/login"}
              className="font-bold text-grey-900 underline underline-offset-2"
            >
              Log In
            </Link>
          </span>
        }
      />

      <div className="mt-10 flex flex-col gap-5">
        {isError && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        {isGoogleAuthError && (
          <InfoComponent
            title="Error"
            text={
              (googleAuthError as unknown as BaseApiErrorResponse).data
                .message ?? "An error occurred!"
            }
            type="error"
          />
        )}

        <Button
          type="button"
          onClick={() => {
            loginWighGoogle();
          }}
          text="Continue with Google"
          variation="plain"
          icon="google"
          loading={authenticatingWithGoogle}
          disabled={authenticatingWithGoogle}
        />

        <OrText />

        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <TextInput
            type="text"
            name="firstName"
            value={values.firstName}
            placeholder="e.g. Olamide"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="First name"
            errors={
              errors.firstName && touched.firstName ? errors.firstName : ""
            }
            autoFocus
            autoFocusOnMobile={false}
          />

          <TextInput
            type="text"
            name="lastName"
            value={values.lastName}
            placeholder="e.g. Uche"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Last name"
            errors={errors.lastName && touched.lastName ? errors.lastName : ""}
          />
        </div>

        <TextInput
          type="text"
          name="email"
          value={values.email}
          placeholder="e.g. example@email.com"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Email"
          errors={errors.email && touched.email ? errors.email : ""}
        />

        <TextInput
          type="password"
          name="password"
          value={values.password}
          placeholder="Enter your password"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Password"
          errors={errors.password && touched.password ? errors.password : ""}
        />

        <div className="mt-5 flex w-full flex-col gap-3 text-center">
          <Button
            type="submit"
            text="Create Account"
            variation="primary"
            className=""
            loading={isLoading}
            disabled={isLoading}
          />

          <span className="text-xs text-grey-700">
            By creating an account, you agree to our{" "}
            <Link to={"/login"} className="underline underline-offset-2">
              Term of use
            </Link>{" "}
            and{" "}
            <Link to={"/login"} className="underline underline-offset-2">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </form>
  );
};

const Verify = ({ email }: { email: string }) => {
  const [canResendCode, setCanResendCode] = useState(false);

  const [verifyEmail, { isLoading, isError, error }] = useVerifyEmailMutation();
  const [
    resendOtp,
    { isLoading: resendingOtp, isError: isResendError, error: resendError },
  ] = useResendOtpMutation();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldError,
  } = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: VerifySignUpSchema,
    onSubmit: async (vals) => {
      const response = await verifyEmail({
        userOtp: vals.code,
      });

      if (response.data?.status === "success") {
        dispatch(
          authSliceActions.signUserIn({
            profile: {
              ...response.data.data,
            },
            token: response.data.data.token,
          }),
        );

        navigate("/home");
      }
    },
  });

  useEffect(() => {
    if (isError) {
      setFieldError(
        "code",
        (error as unknown as BaseApiErrorResponse)?.data?.message ??
          "An error occurred!",
      );
    }
    // eslint-disable-next-line
  }, [isError, error]);
  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Verify your email"
        text={`Enter the code we sent to ${email} to verify your account`}
      />

      <div className="mt-10 flex flex-col gap-5">
        {isResendError && (
          <InfoComponent
            title="Error"
            text={
              (resendError as unknown as BaseApiErrorResponse)?.data?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        <TextInput
          type="text"
          name="code"
          value={values.code}
          placeholder="Enter Verification Code"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Verification Code"
          errors={errors.code && touched.code ? errors.code : ""}
          autoFocus
        />

        <div className="mt-5 flex w-full flex-col gap-4 text-center">
          {!canResendCode && (
            <CountdownTimer
              onClick={() => {}}
              text="Resend code"
              onTimerComplete={() => {
                setCanResendCode(true);
              }}
            />
          )}

          <Button
            type="submit"
            text="Continue"
            variation="primary"
            className=""
            loading={isLoading}
            disabled={isLoading}
          />
        </div>

        {canResendCode && (
          <Button
            type="button"
            text="Resend Code"
            variation="plain"
            className=""
            loading={resendingOtp}
            disabled={resendingOtp}
            onClick={async () => {
              const response = await resendOtp({
                email: email,
                type: "welcome",
              });
              if (response.data?.status === "success") {
                setCanResendCode(false);
              }
            }}
          />
        )}
      </div>
    </form>
  );
};

export const SignupPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("step") === "verify" && searchParams.get("email")) {
      setEmail(searchParams.get("email") ?? "");
      setStep(2);
    }
  }, [searchParams]);

  return (
    <>
      {step === 1 && (
        <Signup
          step={step}
          setStep={setStep}
          setEmail={setEmail}
          searchParams={searchParams}
        />
      )}
      {step === 2 && <Verify email={email} />}
    </>
  );
};
